import React, { FC, useCallback, useMemo, useState } from 'react';
import styles from './index.module.scss';
import { Dropdown } from 'antd';
import ColorPickerOverlay from './ColorPickerOverlay';
import { SearchProfilesColorsDict, SearchProfileColor } from 'src/helpers/constants';
import { useMutation } from '@apollo/client';
import { CHANGE_PROFILE_COLOR } from '../../../../queries';
import { Button, notification } from 'src/common';
import { useTranslation } from 'react-i18next';
import { ISearchProfile } from '../../../../types';
import { trackChangeSearchProfileColor } from 'src/segment/events';
import { faTag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IProps {
  color: SearchProfileColor;
  profileId: string;
}

export const ColorPicker: FC<IProps> = (props: IProps) => {
  const { profileId, color } = props;
  const { t } = useTranslation();

  const [currentColor, setCurrentColor] = useState(color);
  const [dropdownVisible, setDropdownVisible] = useState(false);

  const [changeColorOfSearchProfile] = useMutation<
    { updateSearchProfile: ISearchProfile },
    { id: string; color: SearchProfileColor }
  >(CHANGE_PROFILE_COLOR);

  const onChooseColor = useCallback(
    (color: SearchProfileColor): void => {
      trackChangeSearchProfileColor({
        id: profileId,
        color,
        prevColor: currentColor
      });
      changeColorOfSearchProfile({ variables: { id: profileId, color: color } })
        .then(() => {
          setDropdownVisible(false);
          setCurrentColor(color);
        })
        .catch(() => {
          notification.error({
            description: t('Common.unknownErrorDesc'),
            message: t('Common.unknownError')
          });
        });
    },
    [changeColorOfSearchProfile, currentColor, profileId, t]
  );
  const colorStyle = useMemo(() => color && { color: SearchProfilesColorsDict[color] }, [color]);

  return (
    <Dropdown
      arrow
      trigger={['click']}
      placement={'bottomCenter'}
      getPopupContainer={triggerNode => triggerNode.parentNode as HTMLElement}
      overlay={<ColorPickerOverlay onUpdate={onChooseColor} currentColor={currentColor} />}
      visible={dropdownVisible}
      onVisibleChange={flag => setDropdownVisible(flag)}
    >
      <Button
        type={'link'}
        icon={<FontAwesomeIcon icon={faTag} style={colorStyle} className={styles.colorIndicator} />}
      />
    </Dropdown>
  );
};

export default ColorPicker;
